export type TSortKey = "address" | "stage" | "days_on_market" | "offers" | "viewings";
export type TSortDirection = "asc" | "desc";
export type TSort = { key: TSortKey; direction: TSortDirection };

export type TSortableAddress = {
    building_name: string | null;
    building_number: string | null;
    street_name: string | null;
    city_town: string | null;
    postcode: string | null;
};

export type TSortableCase = {
    bucket: string;
    address: TSortableAddress | null;
    days_on_market: number | null;
    offers_count_last_30d: number;
    viewings_count_last_30d: number;
};

const formatAddressForSort = (address: TSortableAddress | null): string => {
    if (!address) return "";
    const buildingPart = [address.building_number, address.building_name].filter((p): p is string => !!p).join(" ");
    const parts = [buildingPart, address.street_name, address.city_town, address.postcode].filter((p): p is string => !!p && p.trim().length > 0);
    return parts.join(", ");
};

export const compareNullableNumber = (
    a: number | null,
    b: number | null,
    direction: TSortDirection,
): number => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return direction === "asc" ? a - b : b - a;
};

export const sortCases = <T extends TSortableCase>(
    cases: ReadonlyArray<T>,
    sort: TSort,
    bucketOrder: ReadonlyArray<string>,
): T[] => {
    const compare = (a: T, b: T): number => {
        switch (sort.key) {
            case "address": {
                const aStr = formatAddressForSort(a.address).toLocaleLowerCase();
                const bStr = formatAddressForSort(b.address).toLocaleLowerCase();
                return sort.direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
            }
            case "stage": {
                const aIdx = bucketOrder.indexOf(a.bucket);
                const bIdx = bucketOrder.indexOf(b.bucket);
                return sort.direction === "asc" ? aIdx - bIdx : bIdx - aIdx;
            }
            case "days_on_market":
                return compareNullableNumber(a.days_on_market, b.days_on_market, sort.direction);
            case "offers":
                return sort.direction === "asc"
                    ? a.offers_count_last_30d - b.offers_count_last_30d
                    : b.offers_count_last_30d - a.offers_count_last_30d;
            case "viewings":
                return sort.direction === "asc"
                    ? a.viewings_count_last_30d - b.viewings_count_last_30d
                    : b.viewings_count_last_30d - a.viewings_count_last_30d;
        }
    };
    return [...cases].sort(compare);
};

export const nextSortAfterClick = (current: TSort, clickedKey: TSortKey): TSort => {
    if (current.key !== clickedKey) {
        const defaultDirectionForKey: TSortDirection =
            clickedKey === "address" || clickedKey === "stage" ? "asc" : "desc";
        return { key: clickedKey, direction: defaultDirectionForKey };
    }
    return { key: clickedKey, direction: current.direction === "asc" ? "desc" : "asc" };
};
